import React, { Component } from 'react';
import DeviceData from 'drivers/deviceData';
import Storages from 'storages';

import EventBus from 'utils/eventBus';
import StorageKeys from 'enum/storages.enum';
import DeviceTypes from 'enum/device-type.enum';

import { translate as t } from 'utils/i18n';
import { trackPageItem } from 'components/main/common/utils';
import MenuId from 'enum/menu-id.enum';

import {
  createChannelDataService,
  createChannelNavigationService,
  createChannelVisibilityService,
  createDevicePlaybackService,
  updatePlayerState,
  getPlayerState,
  addChannelIndexNumber,
  clearChannelIndex,
  getChannelIndex,
} from './common';
import { playChannelStream, clearHttpChannelLoop, waitUntilStreamReady, watchStreamReadyAfterTimeout, withTimeout, PLAY_ATTEMPT_TIMEOUT_MS, RESET_TIMEOUT_MS } from './common/channelStreamUrl';
import { numKeys } from 'utils/common/numKeys';
import { createDevicePlayer, getDeviceType } from './players/devicePlayerFactory';
import { usesTizenAvPlayerSurface } from 'utils/device/tizenPlatform';

/**
 * Base template for all channel display variations
 * Handles device player control, screen resolution, keyboard navigation, data processing, and visibility
 */
class BaseChannelTemplate extends Component {
  constructor(props) {
    super(props);
    this.subscriptions = {};
    this.sleepTime = 20 * 1000;
    // Initialize services
    this.channelData = createChannelDataService();
    this.channelNav = createChannelNavigationService();
    this.channelVis = createChannelVisibilityService();
    this.devicePlayback = createDevicePlaybackService();

    // Initialize device-specific player
    this.player = createDevicePlayer();

    this.state = {
      focusIndex: 0,
      items: [],
      loading: false,
      redirectBack: false,
      showError: false,
      language_id: 0,
      channelsList: [],
      allowedChannels: 1,
      isDummy: false,
      showList: false,
      slideList: true,
      timeOut: 1 * 1000,
      channelOnly: false,
      channelTimeOut: null,
      channelLoading: false,
      channelSignalUnavailable: false,
    };
    this.onKeyPress = this.onKeyPress.bind(this);
    try {
      console.log('CHANNELS: constructor');
    } catch (e) {}
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyPress);
    // Slide Auto hide and Show The Channel List
    this._isMounted = true;
    this.checkForFocusIn();
    const channelOnly = Storages.MainStorage.getItem(StorageKeys.Keys.CHANNELONLY);
    this.setState({ channelOnly });
    this.calculateNumberOfChannels();
  }

  /**
   * Calculate how many channels should be visible in the carousel
   * For Android: show many, for others: measure based on viewport
   */
  async calculateNumberOfChannels() {
    this.setState({ loading: true });

    if (Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST).isANDROID) {
      this.setState(
        {
          allowedChannels: 2000,
          items: [],
          channelsList: [],
        },
        () => {
          this.subscriptions['refresh'] = EventBus.subscribe('refresh', this.getData.bind(this));
          this.getData();
        }
      );
    } else {
      const sampleItem = {
        active: false,
        channel_id: {
          channel: {
            image: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', // 1px transparent gif
          },
          locale_channel: {
            locale_name: 'Sample Channel',
          },
        },
      };

      const totalChannels = await this.getMeasurements(sampleItem);
      this.setState(
        {
          allowedChannels: totalChannels,
          items: [],
          channelsList: [],
        },
        () => {
          this.subscriptions['refresh'] = EventBus.subscribe('refresh', this.getData.bind(this));
          this.getData();
        }
      );
    }
  }

  async getMeasurements(sampleItem) {
    throw new Error('implement in subclass');
  }

  /**
   * When true, only radio channels are shown/played.
   * Baseline and WarwickV1 set this from the isRadio prop; Warwick redesign uses the same prop.
   */
  isRadioMode() {
    return false;
  }

  /**
   * Returns the channel list that navigation and playback should operate on.
   * Override in subclasses that maintain a filtered/derived item list.
   */
  getActiveItems() {
    return this.state.items;
  }

  trackChannelItem() {
    const { items, focusIndex } = this.state;
    if (items && items[focusIndex]) {
      trackPageItem(MenuId.CHANNELS, items[focusIndex]?.channel_id?.locale_english?.locale_name);
    }
  }

  checkForFocusIn() {
    this.checkPointer();
    this.slideShowList();
    this.subscriptions['mouseMoved'] = EventBus.subscribe('mouseMoved', () => {
      clearTimeout(this.state.timeOut);
      this.slideShowList();
    });
  }

  async getData() {
    try {
      let { focusIndex } = this.state;
      this.setState({ loading: true });

      // Get and process channels using extracted hook
      const orgChannels = this.channelData.getOrgChannels();

      // Handle empty channels case
      if (!orgChannels.length) {
        // If the channel catalog disappears (often due to a background refresh/network failure),
        // stop playback so we don't end up with "error UI" while audio continues.
        await this.stopPlayer();

        this.setState({
          showError: false,
          loading: false,
          items: [],
          channelsList: [],
        });
        return;
      }

      // Process channels with locales
      const processedChannels = this.channelData.processChannelsWithLocales(orgChannels);
      const showError = processedChannels.length === 0;
      const modeChannels = this.channelData.filterChannelsByMode(
        processedChannels,
        this.isRadioMode()
      );

      if (focusIndex >= modeChannels.length) {
        focusIndex = 0;
      }

      this.setState({ showError });

      const channelsList = await this.generateNewChannelList(focusIndex, modeChannels);

      this.setState(
        {
          items: modeChannels,
          channelsList,
          loading: false,
          focusIndex,
        },
        () => {
          this.checkDevice(focusIndex);
        }
      );
    } catch (error) {
      this.setState({
        items: [],
        loading: false,
      });
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { focusIndex, items } = this.state;

    if ((items.length > 0 && items !== prevState.items) || focusIndex !== prevState.focusIndex) {
      this.trackChannelItem();
    }
  }

  checkPointer() {
    const showList = this.channelVis.checkPointer();
    this.setState({ showList });
  }

  componentWillUnmount() {
    // fix Warning: Can't perform a React state update on an unmounted component
    window.removeEventListener('keydown', this.onKeyPress);
    clearInterval(this.interval);
    this._clearStreamReadyWatch();

    Object.keys(this.subscriptions).forEach((key) => {
      if (this.subscriptions[key] && typeof this.subscriptions[key].unsubscribe === 'function') {
        this.subscriptions[key].unsubscribe();
      }
    });

    this.setState = (state, callback) => {
      state.items = [];
      state.channelsList = [];
      return;
    };
    this._isMounted = false;
  }

  _clearStreamReadyWatch() {
    if (this._cancelStreamReadyWatch) {
      this._cancelStreamReadyWatch();
      this._cancelStreamReadyWatch = null;
    }
  }

  /**
   * Build the callbacks object passed to the navigation service.
   * Subclasses can override this to add or replace individual callbacks
   * without duplicating the full onKeyPress logic.
   */
  getKeyPressCallbacks() {
    return {
      onVerticalMove: (delta) => this.changeChannelIndex(this.state.focusIndex + delta),
      onHorizontalMove: (delta) => this.changeChannelIndex(this.state.focusIndex + delta),
      onEnter: () => this.screenResolution(),
      onBack: (channelOnly) => this.checkResolution(!channelOnly),
      onChannelNumber: async (keyCode) => {
        let { channelTimeOut } = this.state;
        clearTimeout(channelTimeOut);
        const tvKeyControls = await DeviceData.getTVControl();
        const channelKeys = numKeys(tvKeyControls);

        if (channelKeys[keyCode] > -1) {
          addChannelIndexNumber(channelKeys[keyCode]);
          channelTimeOut = setTimeout(() => {
            clearTimeout(channelTimeOut);
            this.setChannelIndex(getChannelIndex());
            clearChannelIndex();
          }, 1000);
        }
        this.setState({ channelTimeOut });
      },
    };
  }

  async onKeyPress(event) {
    if (!this.state.showList) {
      this.setState({ showList: true });
      this.slideShowList();
    }
    await this.channelNav.handleKeyPress(event, this.state, this.getKeyPressCallbacks());
  }

  async changeChannelIndex(newIndex) {
    const activeItems = this.getActiveItems();
    if (!activeItems || activeItems.length === 0) return;

    newIndex = this.channelNav.wrapChannelIndex(newIndex, activeItems.length);

    // Skip if already on this channel — avoids unnecessary reload
    if (newIndex === this.state.focusIndex) return;

    const channelsList = await this.generateNewChannelList(newIndex, activeItems);
    if (newIndex >= 0 && newIndex < activeItems.length) {
      this.setState({
        focusIndex: newIndex,
        channelsList,
      });
      clearTimeout(this.state.timeOut);
      this.slideShowList();
      this.checkDevice(newIndex);
    }
  }

  setChannelIndex(channelIndx) {
    const index = this.channelNav.getChannelIndexFromNumbers(channelIndx);
    this.changeChannelIndex(index);
  }

  async generateNewChannelList(newIndex, list) {
    throw new Error('implement in subclass');
  }

  async checkDevice(focusIndex) {
    const activeItems = this.getActiveItems();
    // Fresh state each play — do not reuse a stale snapshot from earlier in the call stack
    const playerState = getPlayerState();

    const url = (activeItems ?? [])[focusIndex]?.channel_id?.channel?.channel_url;

    updatePlayerState({ url });

    if (!url) {
      console.warn('CHANNELS: No channel URL — skip play');
      if (this._isMounted) {
        this.setState({ channelLoading: false, channelSignalUnavailable: true });
      }
      return;
    }

    const deviceType = getDeviceType();
    // Brief floor so channel loading overlay fade-in is visible on instant starts
    var minLoadingMs = 400;

    // Special handling for Android and Tizen initialization
    if (deviceType === DeviceTypes.ANDROID) {
      const channelsList = await this.generateNewChannelList(0, activeItems);
      this.player.launchNativeActivity(channelsList);
      this.setState({ redirectBack: !this.state.channelOnly });
      return;
    }

    // Use device player to start playback - all device-specific logic is now in player classes
    var loadingStartedAt = Date.now();
    this._channelLoadingToken = (this._channelLoadingToken || 0) + 1;
    var loadingToken = this._channelLoadingToken;
    var self = this;
    var streamReady = false;
    this._clearStreamReadyWatch();
    // Clear leftover busy / playtime from a failed previous channel so the next zap can recover
    updatePlayerState({
      streamReady: false,
      httpPlayTimeMs: 0,
      isPlayerBusy: false,
    });
    this.setState({ channelLoading: true, channelSignalUnavailable: false });

    // Hard reset native decoder before every tune — prevents stuck avplay after dead streams
    // (TEP keeps preview layout flags via resetForNextChannel)
    try {
      clearHttpChannelLoop(updatePlayerState);
      if (this.player && typeof this.player.resetForNextChannel === 'function') {
        await withTimeout(
          this.player.resetForNextChannel(),
          RESET_TIMEOUT_MS,
          'resetForNextChannel timeout'
        );
      } else if (this.player && typeof this.player.stop === 'function') {
        await withTimeout(this.player.stop(), RESET_TIMEOUT_MS, 'player.stop timeout');
      }
    } catch (stopErr) {
      console.warn('CHANNELS: pre-play reset warning:', stopErr && stopErr.message);
    }
    if (loadingToken !== this._channelLoadingToken) {
      return;
    }

    try {
      // TEP: wait for layout only on first entry (landing → channels). Channel zaps skip this.
      if (
        deviceType === DeviceTypes.TIZEN_TEP &&
        !(this.player && typeof this.player.isTepLayoutReady === 'function' && this.player.isTepLayoutReady())
      ) {
        await new Promise(function (resolve) {
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                requestAnimationFrame(resolve);
              });
            });
          } else {
            setTimeout(resolve, 48);
          }
        });
      }
      if (loadingToken !== this._channelLoadingToken) {
        return;
      }
      await withTimeout(
        playChannelStream(
          this.player,
          url,
          getPlayerState().displayFullScreen,
          updatePlayerState
        ),
        PLAY_ATTEMPT_TIMEOUT_MS,
        'playChannelStream timeout'
      );
      if (loadingToken !== this._channelLoadingToken) {
        return;
      }
      // Hold spinner until buffering/first frame (abort if user zaps away)
      streamReady = await waitUntilStreamReady(this.player, null, function () {
        return loadingToken === self._channelLoadingToken;
      });
    } catch (error) {
      console.error('CHANNELS: Error playing media:', error);
      streamReady = false;
      // Force-clear native player so the next channel can start without reboot
      try {
        updatePlayerState({ isPlayerBusy: false, streamReady: false });
        if (this.player && typeof this.player.resetForNextChannel === 'function') {
          await withTimeout(
            this.player.resetForNextChannel(),
            RESET_TIMEOUT_MS,
            'post-fail reset timeout'
          );
        }
      } catch (resetErr) {
        console.warn('CHANNELS: post-fail reset warning:', resetErr && resetErr.message);
      }
    } finally {
      if (loadingToken !== this._channelLoadingToken) {
        return;
      }
      var elapsed = Date.now() - loadingStartedAt;
      var waitMs = minLoadingMs - elapsed;
      if (waitMs > 0) {
        await new Promise(function (resolve) {
          setTimeout(resolve, waitMs);
        });
      }
      if (loadingToken !== this._channelLoadingToken) {
        return;
      }
      if (this._isMounted) {
        this.setState({
          channelLoading: false,
          channelSignalUnavailable: !streamReady,
        });
        // If stream starts after the 8s UI timeout, clear the banner.
        // Native reset happens at the *start* of the next checkDevice (channel zap),
        // so a dead tune cannot permanently lock avplay for all channels.
        if (!streamReady) {
          updatePlayerState({ isPlayerBusy: false });
          this._cancelStreamReadyWatch = watchStreamReadyAfterTimeout(
            this.player,
            function () {
              if (!self._isMounted || loadingToken !== self._channelLoadingToken) {
                return;
              }
              self.setState({ channelSignalUnavailable: false });
              self._cancelStreamReadyWatch = null;
            },
            20000
          );
        }
      }
    }
  }

  slideShowList() {
    const playerState = getPlayerState();
    if (playerState.displayFullScreen) {
      if (this._isMounted) {
        this.setState({
          slideList: true,
        });

        this.setState({
          timeOut: setTimeout(() => {
            if (this._isMounted) {
              this.setState({
                slideList: false,
              });
            }
          }, this.sleepTime),
        });
      }
    } else {
      if (this._isMounted) {
        this.setState({
          slideList: true,
        });
      }
    }
  }

  async screenResolution() {
    const deviceType = getDeviceType();

    if (this.player.supportsFullscreenToggle()) {
      await this.player.toggleFullscreen();
      this.slideShowList();
    } else if (deviceType === DeviceTypes.WEBOS) {
      console.log('WEBOS: Streams are always fullscreen, channel list remains visible');
      this.slideShowList();
    }
  }

  async checkResolution(redirectBack) {
    if (!redirectBack) return;

    const playerState = getPlayerState();

    if (this.player.supportsFullscreenToggle()) {
      if (playerState.displayFullScreen) {
        await this.player.toggleFullscreen();
        this.slideShowList();
      } else {
        await this.stopPlayer();
        this.setState({ redirectBack });
      }
    } else {
      // For webOS and other devices, stop player and redirect
      await this.stopPlayer();
      this.setState({ redirectBack });
    }
  }

  async stopPlayer() {
    clearHttpChannelLoop(updatePlayerState);
    try {
      await this.player.stop();
    } catch (e) {
      // Best-effort stop — never block UI state on device player errors.
    }
  }

  /**
   * Render error/empty states
   * Common across all templates
   */
  renderErrorStates() {
    const { loading, showError, items } = this.state;
    return (
      <>
        {items.length <= 0 && !showError && !loading && (
          <div className={`has-text-danger has-text-centered`}>
            <p className={'is-size-4 has-text-bold'}>{t('Channels.channels_error')}</p>
          </div>
        )}
        {items.length <= 0 && showError && !loading && (
          <div className={`has-text-danger has-text-centered`}>
            <p className={'is-size-4 has-text-bold'}>
              The selected language is not configured yet.
            </p>
          </div>
        )}
      </>
    );
  }

  /**
   * Get player container elements (HTML divs for device players)
   * Can be overridden for custom player layout
   */
  getPlayerContainers() {
    const deviceList = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST);
    const forceTizenBrowserTest = process.env.REACT_APP_FORCE_TIZEN_PLAYER === 'true';
    const useTizenAvPlayer = usesTizenAvPlayerSurface(deviceList) || forceTizenBrowserTest;
    const isDefaultPlayer =
      !deviceList.isSAMSUNG && !deviceList.isWEBOS && !deviceList.isANDROID && !useTizenAvPlayer;

    return (
      <>
        {deviceList.isSAMSUNG && <div id='player_container' ref='player' />}
        {useTizenAvPlayer && (
          <object
            id='av-player'
            type='application/avplayer'
            className={deviceList.isTIZEN_TEP ? 'av-player-tep' : undefined}
            aria-label='Tizen AV Player'
          />
        )}
        {deviceList.isWEBOS && (
          <div
            id='webos-media-container'
            style={{
              position: 'fixed',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              background: 'transparent',
              zIndex: -1,
              pointerEvents: 'none',
            }}
          >
            {/* webOS fullscreen video renders behind this container */}
          </div>
        )}
        {isDefaultPlayer && (
          <video
            id='default-video-player'
            style={{
              width: '100%',
              height: '100%',
              background: '#000',
            }}
            controls={false}
            autoPlay={false}
            playsInline
          />
        )}
      </>
    );
  }

  /**
   * Main render method - MUST be implemented by subclass
   * Subclass has full control over layout structure
   */
  render() {
    throw new Error('render() must be implemented in subclass');
    // eslint-disable-next-line no-unreachable
    return null;
  }
}

export default BaseChannelTemplate;
